"use client";

import { createContext, useContext, useCallback, useState, useMemo, type ReactNode } from "react";
import { Toast, type ToastOptions } from "./Toast.js";

// Re-export ToastOptions for external consumers
export type { ToastOptions };

export interface ToastActions {
    addToast: (options: ToastOptions) => string;
    removeToast: (id: string) => void;
    clearAllToasts: () => void;
}

const ToastActionsContext = createContext<ToastActions | undefined>(undefined);
const ActiveToastsContext = createContext<Toast[] | undefined>(undefined);

export function useToastActions(): ToastActions {
    const context = useContext(ToastActionsContext);
    if (!context)
        throw new Error("useToastActions must be used within a ToastProvider");

    return context;
}

export function useActiveToasts(): Toast[] {
    const context = useContext(ActiveToastsContext);
    if (!context)
        throw new Error("useActiveToasts must be used within a ToastProvider");

    return context;
}

interface ToastProviderProps {
    children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const addToast = useCallback((options: ToastOptions): string => {
        const newToast = new Toast(options);
        setToasts(prev => [...prev, newToast]);
        return newToast.id;
    }, []);

    const removeToast = useCallback((id: string): void => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const clearAllToasts = useCallback((): void => {
        setToasts([]);
    }, []);

    const actions = useMemo<ToastActions>(() => ({
        addToast,
        removeToast,
        clearAllToasts,
    }), [addToast, removeToast, clearAllToasts]);

    return (
        <ToastActionsContext.Provider value={actions}>
            <ActiveToastsContext.Provider value={toasts}>
                {children}
            </ActiveToastsContext.Provider>
        </ToastActionsContext.Provider>
    );
}
